import { useEffect, useRef, useState } from "react";
import { FolderOpen, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { AttachmentChips } from "@/components/files/AttachmentChips";
import { FilePickerDialog } from "@/components/files/FilePickerDialog";
import { FileStatusBadge } from "@/components/files/FileStatusBadge";
import { useAddProjectFiles, useRemoveProjectFile } from "@/hooks/queries";
import { useUploads } from "@/hooks/useUploads";
import { ACCEPT, FILE_KINDS, formatBytes } from "@/lib/files";

function useLinkUploads(projectId, uploads) {
  const add = useAddProjectFiles();
  const linked = useRef(new Set());
  useEffect(() => {
    uploads.items.forEach((it) => {
      if (!it.id || it.status === "uploading" || it.status === "error" || linked.current.has(it.id)) return;
      linked.current.add(it.id);
      add.mutateAsync({ id: projectId, fileIds: [it.id] }).then(() => uploads.remove(it.key)).catch((e) => toast.error(e.message));
    });
  }, [uploads, projectId, add]);
}

export function ProjectFiles({ projectId, files }) {
  const inputRef = useRef(null);
  const [picking, setPicking] = useState(false);
  const uploads = useUploads();
  const add = useAddProjectFiles();
  const remove = useRemoveProjectFile();
  useLinkUploads(projectId, uploads);
  const pick = (picked) => picked.length && add.mutateAsync({ id: projectId, fileIds: picked.map((f) => f.id) }).catch((e) => toast.error(e.message));
  return (
    <div data-testid="project-files">
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => inputRef.current?.click()} className="inline-flex items-center gap-2 rounded-full border border-white/12 px-3.5 py-1.5 text-[13px] text-zinc-200 hover:bg-white/[0.05]" data-testid="project-files-upload">
          <Upload className="h-3.5 w-3.5" /> Upload
        </button>
        <button type="button" onClick={() => setPicking(true)} className="inline-flex items-center gap-2 rounded-full border border-white/12 px-3.5 py-1.5 text-[13px] text-zinc-200 hover:bg-white/[0.05]" data-testid="project-files-add-existing">
          <FolderOpen className="h-3.5 w-3.5" /> Add from Files
        </button>
        <input ref={inputRef} type="file" multiple accept={ACCEPT} className="hidden" data-testid="project-files-input" onChange={(e) => { uploads.addFiles(e.target.files); e.target.value = ""; }} />
      </div>
      <AttachmentChips items={uploads.items} onRemove={uploads.remove} className="mt-3" />
      <div className="mt-4 space-y-0.5">
        {files.length === 0 && !uploads.items.length && <p className="py-8 text-center text-sm text-zinc-500" data-testid="project-files-empty">No files yet. ChatSleek will use files you add here in every chat in this project.</p>}
        {files.map((f) => {
          const Icon = FILE_KINDS[f.kind]?.icon || FILE_KINDS.txt.icon;
          return (
            <div key={f.id} className="group flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-white/[0.035]" data-testid={`project-file-${f.id}`}>
              <Icon className="h-4 w-4 shrink-0 text-zinc-400" strokeWidth={1.6} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-zinc-100">{f.name}</span>
                <span className="block text-[11.5px] text-zinc-500">{FILE_KINDS[f.kind]?.label} · {formatBytes(f.size_bytes)}</span>
              </span>
              <FileStatusBadge file={f} />
              <button type="button" onClick={() => remove.mutateAsync({ id: projectId, fileId: f.id }).catch((e) => toast.error(e.message))} aria-label={`Remove ${f.name} from project`} className="rounded-md p-1 text-zinc-500 hover:bg-white/[0.06] hover:text-white" data-testid={`project-file-remove-${f.id}`}>
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
      <FilePickerDialog open={picking} onOpenChange={setPicking} onPick={pick} excludeIds={files.map((f) => f.id)} />
    </div>
  );
}
